import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { FileText, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CollaborativeEditor,
  CollaborativeEditorRef,
} from "@/components/editors/CollaborativeEditor";
import {
  GlobalDocument,
  GlobalDocumentLinkType,
  useCreateGlobalDocument,
  useCreateGlobalDocumentLink,
  useGlobalDocumentLinksByEntity,
  useGlobalDocuments,
} from "@/hooks/useGlobalDocuments";
import {
  MeetingNoteWithTags,
  useCreateMeetingNote,
  useProjectMeetingNotes,
  useRelatedProjectMeetingNotes,
} from "@/hooks/useMeetingNotes";
import { ProjectWithRelations } from "@/hooks/useProjects";
import { MeetingNoteCard } from "./meeting-notes/MeetingNoteCard";

interface ProjectNotesTabProps {
  project: ProjectWithRelations;
}

type DocumentSectionProps = {
  project: ProjectWithRelations;
  linkType: GlobalDocumentLinkType;
  title: string;
  placeholder: string;
  active: boolean;
};

export function ProjectNotesTab({ project }: ProjectNotesTabProps) {
  const [activeTab, setActiveTab] = useState("meeting-notes");

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
      <div className="overflow-x-auto">
        <TabsList className="w-max justify-start">
          <TabsTrigger value="meeting-notes">Meeting Notes</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
          <TabsTrigger value="debriefing">Debriefing</TabsTrigger>
          <TabsTrigger value="related-notes">Related Notes</TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="meeting-notes" className="mt-0">
        <ProjectMeetingNotesSection project={project} />
      </TabsContent>
      <TabsContent value="notes" className="mt-0">
        <ProjectDocumentSection
          project={project}
          linkType="project_note"
          title={`Notes - ${project.name}`}
          placeholder="Start writing project notes..."
          active={activeTab === "notes"}
        />
      </TabsContent>
      <TabsContent value="debriefing" className="mt-0">
        <ProjectDocumentSection
          project={project}
          linkType="project_debriefing"
          title={`Debriefing - ${project.name}`}
          placeholder="Start writing the debriefing..."
          active={activeTab === "debriefing"}
        />
      </TabsContent>
      <TabsContent value="related-notes" className="mt-0">
        <RelatedMeetingNotesSection project={project} />
      </TabsContent>
    </Tabs>
  );
}

function ProjectMeetingNotesSection({ project }: ProjectNotesTabProps) {
  const navigate = useNavigate();
  const { data: notes = [], isLoading } = useProjectMeetingNotes(project.id);
  const createMeetingNote = useCreateMeetingNote();

  const sortedNotes = useMemo(() => {
    return [...notes].sort(
      (a, b) =>
        new Date(b.meeting_date).getTime() - new Date(a.meeting_date).getTime(),
    );
  }, [notes]);

  const handleCreateNote = () => {
    createMeetingNote.mutate(
      {
        title: "Untitled Meeting Note",
        meeting_date: format(new Date(), "yyyy-MM-dd"),
        project_id: project.id,
      },
      {
        onSuccess: (note) => navigate(`/meeting-notes/${note.id}?edit=1`),
      },
    );
  };

  const handleJumpToNote = (noteId: string) => {
    document
      .getElementById(`meeting-note-${noteId}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (isLoading) return <NotesSkeleton />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">Meeting Notes</h2>
          {sortedNotes.length > 0 && (
            <Select onValueChange={handleJumpToNote}>
              <SelectTrigger className="w-[250px]">
                <SelectValue placeholder="Jump to note..." />
              </SelectTrigger>
              <SelectContent>
                {sortedNotes.map((note) => (
                  <SelectItem key={note.id} value={note.id}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(note.meeting_date), "MMM d")}
                      </span>
                      <span className="truncate max-w-[150px]">
                        {note.title}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <Button
          offlineEdit
          onClick={handleCreateNote}
          disabled={createMeetingNote.isPending}
          data-testid="create-meeting-note-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          {createMeetingNote.isPending ? "Creating..." : "New Meeting Note"}
        </Button>
      </div>

      <MeetingNoteList
        notes={sortedNotes}
        emptyTitle="No meeting notes yet"
        emptyDescription="Create your first meeting note to track discussions and decisions for this project."
      />
    </div>
  );
}

function RelatedMeetingNotesSection({ project }: ProjectNotesTabProps) {
  const { data: notes = [], isLoading } = useRelatedProjectMeetingNotes(
    project.id,
  );

  const relatedNotes = useMemo(
    () => notes.filter((note) => note.project_id !== project.id),
    [notes, project.id],
  );

  if (isLoading) return <NotesSkeleton />;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Related Notes</h2>
        <p className="text-sm text-muted-foreground">
          Meeting notes where this project was mentioned with slash linking.
        </p>
      </div>
      <MeetingNoteList
        notes={relatedNotes}
        emptyTitle="No related notes yet"
        emptyDescription="Meeting notes that mention this project will appear here."
      />
    </div>
  );
}

function MeetingNoteList({
  notes,
  emptyTitle,
  emptyDescription,
}: {
  notes: MeetingNoteWithTags[];
  emptyTitle: string;
  emptyDescription: string;
}) {
  if (notes.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <CardTitle className="text-lg mb-1">{emptyTitle}</CardTitle>
          <p className="text-muted-foreground text-sm max-w-sm">
            {emptyDescription}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {notes.map((note) => (
        <MeetingNoteCard key={note.id} note={note} collapsible />
      ))}
    </div>
  );
}

function ProjectDocumentSection({
  project,
  linkType,
  title,
  placeholder,
  active,
}: DocumentSectionProps) {
  const { data: docs = [], isLoading: docsLoading } = useGlobalDocuments(true);
  const { data: links = [], isLoading: linksLoading } =
    useGlobalDocumentLinksByEntity(linkType, project.id);
  const createDoc = useCreateGlobalDocument();
  const createLink = useCreateGlobalDocumentLink();
  const autoCreateStarted = useRef(false);

  const linkedDoc = useMemo(() => {
    const linkedIds = new Set(links.map((link) => link.global_document_id));
    return docs
      .filter((doc) => linkedIds.has(doc.id) && !doc.is_folder)
      .sort((a, b) => a.created_at.localeCompare(b.created_at))[0];
  }, [docs, links]);

  const handleCreateDocument = useCallback(async () => {
    const doc = await createDoc.mutateAsync({
      title,
      is_folder: false,
      hidden_from_docs: true,
    });
    await createLink.mutateAsync({
      global_document_id: doc.id,
      link_type: linkType,
      link_id: project.id,
    });
  }, [createDoc, createLink, linkType, project.id, title]);

  useEffect(() => {
    if (!active) return;
    if (docsLoading || linksLoading) return;
    if (linkedDoc) return;
    if (autoCreateStarted.current) return;

    autoCreateStarted.current = true;
    void handleCreateDocument();
  }, [active, docsLoading, handleCreateDocument, linksLoading, linkedDoc]);

  if (docsLoading || linksLoading) return <NotesSkeleton />;

  if (!linkedDoc) return <NotesSkeleton />;

  return <ProjectDocumentEditor doc={linkedDoc} placeholder={placeholder} />;
}

function ProjectDocumentEditor({
  doc,
  placeholder,
}: {
  doc: GlobalDocument;
  placeholder: string;
}) {
  const editorRef = useRef<CollaborativeEditorRef>(null);

  return (
    <Card>
      <CardContent className="p-4">
        <CollaborativeEditor
          ref={editorRef}
          documentId={doc.document_id}
          editable={true}
          placeholder={placeholder}
        />
      </CardContent>
    </Card>
  );
}

function NotesSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-10 w-40" />
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}
